import React, { useState } from 'react';
import { CalendarClock, Clock, MapPin, CheckCircle2, Shuffle } from 'lucide-react';
import { parseLocalDateTime, formatTime } from '../../utils/timeUtils';
import type { RoutyFillCandidatesArtifact, RoutyFillCandidate } from '../../business/routy/routyArtifacts';

interface FillCandidatesCardProps {
  artifact: RoutyFillCandidatesArtifact;
  disabled?: boolean;
  onSelect: (artifact: RoutyFillCandidatesArtifact, candidate: RoutyFillCandidate) => void;
}

const FillCandidatesCard: React.FC<FillCandidatesCardProps> = ({ artifact, disabled, onSelect }) => {
  const [pickedId, setPickedId] = useState<string | null>(null);
  const locked = disabled || pickedId !== null;

  const gapStart = parseLocalDateTime(artifact.date, artifact.gapStartTime);
  const gapEnd = parseLocalDateTime(artifact.date, artifact.gapEndTime);

  if (artifact.candidates.length === 0) return null;

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <Shuffle className="w-4 h-4 text-ink-500" />
        <span className="text-sm font-semibold text-ink-900">Fill your gap</span>
        {gapStart && gapEnd && (
          <span className="ml-auto text-2xs font-medium text-ink-500 bg-surface border border-ink-200 px-2 py-0.5 rounded-md">
            {formatTime(gapStart)} – {formatTime(gapEnd)}
          </span>
        )}
      </div>

      <div className="p-3 space-y-2.5">
        {artifact.candidates.map((cand) => {
          const proposed = parseLocalDateTime(artifact.date, cand.proposedStartTime);
          const isPicked = pickedId === cand.appointmentId;
          return (
            <div
              key={cand.appointmentId}
              className={`rounded-lg border p-3 transition-colors ${
                isPicked ? 'border-emerald-300 ring-1 ring-emerald-200 bg-emerald-50/40' : 'border-ink-200 bg-surface'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-ink-900 truncate">{cand.contactName}</p>
                {cand.proximityMiles != null && (
                  <span className="flex items-center gap-1 flex-shrink-0 text-2xs text-ink-500">
                    <MapPin className="w-3 h-3" />
                    {cand.proximityMiles.toFixed(1)} mi
                  </span>
                )}
              </div>

              <p className="mt-0.5 text-xs text-ink-500">Currently {cand.currentLabel}</p>

              <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
                <CalendarClock className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                <span className="text-xs font-medium text-ink-800">
                  Move to {proposed ? formatTime(proposed) : cand.proposedStartTime}
                  {cand.driveInMinutes != null ? ` · ${cand.driveInMinutes} min drive in` : ''}
                </span>
              </div>

              <button
                onClick={() => {
                  setPickedId(cand.appointmentId);
                  onSelect(artifact, cand);
                }}
                disabled={locked}
                className={`mt-2.5 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all active:scale-[0.99] disabled:cursor-not-allowed ${
                  isPicked
                    ? 'bg-emerald-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40'
                }`}
              >
                {isPicked ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Selected
                  </>
                ) : (
                  <>
                    <Clock className="w-3.5 h-3.5" />
                    Move into this gap
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FillCandidatesCard;
